import { useState, useEffect } from 'react';
import { provinciaEclesiasticaService, diocesisService, lumisialService } from '../services';
import type { ProvinciaEclesiastica, Diocesis, Lumisial } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Tabs } from '../components/ui/Tabs';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Tooltip } from '../components/ui/Tooltip';
import { Select } from '../components/ui/Select';
import { Combobox } from '../components/ui/Combobox';
import { ProvinciaEclesiasticaModal } from '../components/ProvinciaEclesiasticaModal';
import { DiocesisModal } from '../components/DiocesisModal';
import { LumisialModal } from '../components/LumisialModal';
import EliminacionDefinitivaModal from '../components/admin/EliminacionDefinitivaModal';
import { Globe, MapPin, Building2, Plus, CheckCircle, XCircle, Filter, Pencil, Power, PowerOff, Trash2 } from 'lucide-react';
import { matchesSearch } from '../utils/textNormalization';
import { useAuth } from '../context/AuthContext';
import { isAdminGlobal } from '../utils/roleHierarchy';

export function AdminGeografiaPage() {
  const { usuario } = useAuth();
  const [activeTab, setActiveTab] = useState('provincias_eclesiasticas');
  const [provinciasEclesiasticas, setProvinciasEclesiasticas] = useState<ProvinciaEclesiastica[]>([]);
  const [diocesis, setDiocesis] = useState<Diocesis[]>([]);
  const [lumisiales, setLumisiales] = useState<Lumisial[]>([]);
  const [lumisialesFiltrados, setLumisialesFiltrados] = useState<Lumisial[]>([]);
  const [regionesDisponibles, setRegionesDisponibles] = useState<{value: string, label: string}[]>([]);
  const [regionesFiltradas, setRegionesFiltradas] = useState<{value: string, label: string}[]>([]);
  const [provinciasOptions, setProvinciasOptions] = useState<{value: string, label: string}[]>([]);
  const [diocesisOptions, setDiocesisOptions] = useState<{value: string, label: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isProvinciaEclesiasticaModalOpen, setIsProvinciaEclesiasticaModalOpen] = useState(false);
  const [isDiocesisModalOpen, setIsDiocesisModalOpen] = useState(false);
  const [isLumisialModalOpen, setIsLumisialModalOpen] = useState(false);
  const [isEliminacionModalOpen, setIsEliminacionModalOpen] = useState(false);
  const [selectedProvinciaEclesiastica, setSelectedProvinciaEclesiastica] = useState<ProvinciaEclesiastica | undefined>(undefined);
  const [selectedDiocesis, setSelectedDiocesis] = useState<Diocesis | undefined>(undefined);
  const [selectedLumisial, setSelectedLumisial] = useState<Lumisial | undefined>(undefined);
  const [entidadParaEliminar, setEntidadParaEliminar] = useState<{
    tipo: 'provincia' | 'diocesis' | 'lumisial';
    id: string;
    nombre: string;
  } | null>(null);
  const [filtros, setFiltros] = useState({
    provincia_eclesiastica_id: '',
    diocesis_id: '',
    region: '',
    estado: 'todos'
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    aplicarFiltros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lumisiales, filtros]);

  useEffect(() => {
    // Actualizar diócesis cuando cambia la provincia seleccionada
    let diocesisFiltradas = diocesis;
    
    // Filtrar por provincia si está seleccionada
    if (filtros.provincia_eclesiastica_id) {
      diocesisFiltradas = diocesisFiltradas.filter(d => d.provincia_eclesiastica_id === filtros.provincia_eclesiastica_id);
    }
    
    setDiocesisOptions([
      { value: '', label: 'Todas' },
      ...diocesisFiltradas.map(d => ({
        value: d.id,
        label: `${d.nombre} (${d.tipo === 'diocesis' ? 'Diócesis' : 'Prefectura'})`
      }))
    ]);
  }, [filtros.provincia_eclesiastica_id, diocesis]);

  useEffect(() => {
    // Actualizar regiones cuando cambia la diócesis seleccionada
    if (filtros.diocesis_id) {
      // Obtener regiones de los lumisiales que pertenecen a la diócesis seleccionada
      const lumisialesEnDiocesis = lumisiales.filter(l => l.diocesis_id === filtros.diocesis_id);
      const regionesEnDiocesis = [...new Set(lumisialesEnDiocesis
        .map(l => l.region)
        .filter((region): region is string => Boolean(region))
      )].sort();
      
      setRegionesFiltradas([
        { value: '', label: 'Todas' },
        ...regionesEnDiocesis.map(region => ({
          value: region,
          label: region
        }))
      ]);
    } else {
      // Si no hay diócesis seleccionada, mostrar todas las regiones
      setRegionesFiltradas(regionesDisponibles);
    }
  }, [filtros.diocesis_id, lumisiales, regionesDisponibles]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [provinciasData, diocesisData, lumisialesData] = await Promise.all([
        provinciaEclesiasticaService.listarTodos(),
        diocesisService.listarConCantidadLumisiales(),
        lumisialService.listarConDetalles()
      ]);
      
      setProvinciasEclesiasticas(provinciasData);
      setDiocesis(diocesisData);
      setLumisiales(lumisialesData);
      
      // Preparar opciones para provincias eclesiásticas
      setProvinciasOptions([
        { value: '', label: 'Todas' },
        ...provinciasData.map(p => ({
          value: p.id,
          label: p.nombre
        }))
      ]);
      
      // Preparar opciones para diócesis
      setDiocesisOptions([
        { value: '', label: 'Todas' },
        ...diocesisData.map(d => ({
          value: d.id,
          label: `${d.nombre} (${d.tipo === 'diocesis' ? 'Diócesis' : 'Prefectura'})`
        }))
      ]);
      
      // Extraer regiones únicas de los lumisiales
      const regionesUnicas = [...new Set(lumisialesData
        .map(l => l.region)
        .filter((region): region is string => Boolean(region))
      )].sort();
      
      setRegionesDisponibles([
        { value: '', label: 'Todas' },
        ...regionesUnicas.map(region => ({
          value: region,
          label: region
        }))
      ]);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setToast({ type: 'error', message: 'Error al cargar los datos' });
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = () => {
    let resultado = [...lumisiales];

    if (filtros.provincia_eclesiastica_id) {
      resultado = resultado.filter(l => l.provincia_eclesiastica_id === filtros.provincia_eclesiastica_id);
    }

    if (filtros.diocesis_id) {
      resultado = resultado.filter(l => l.diocesis_id === filtros.diocesis_id);
    }

    if (filtros.region) {
      resultado = resultado.filter(l => matchesSearch(l.region, filtros.region));
    }

    if (filtros.estado === 'activos') {
      resultado = resultado.filter(l => l.activo);
    } else if (filtros.estado === 'inactivos') {
      resultado = resultado.filter(l => !l.activo);
    }

    setLumisialesFiltrados(resultado);
  };

  const handleToggleProvinciaEclesiasticaActivo = async (id: string, activo: boolean) => {
    try {
      if (activo) {
        await provinciaEclesiasticaService.desactivar(id);
      } else {
        await provinciaEclesiasticaService.activar(id);
      }
      await cargarDatos();
      setToast({
        type: 'success',
        message: `Provincia eclesiástica ${activo ? 'desactivada' : 'activada'} exitosamente`
      });
    } catch (error) {
      console.error('Error al cambiar estado de la provincia eclesiástica:', error);
      setToast({ type: 'error', message: 'Error al cambiar estado del país' });
    }
  };

  const handleToggleDiocesisActiva = async (id: string, activo: boolean) => {
    try {
      if (activo) {
        await diocesisService.desactivar(id);
      } else {
        await diocesisService.activar(id);
      }
      await cargarDatos();
      setToast({
        type: 'success',
        message: `Diócesis ${activo ? 'desactivada' : 'activada'} exitosamente`
      });
    } catch (error) {
      console.error('Error al cambiar estado de la diócesis:', error);
      setToast({ type: 'error', message: 'Error al cambiar estado de la diócesis' });
    }
  };

  const handleToggleLumisialActivo = async (id: string, activo: boolean) => {
    try {
      if (activo) {
        await lumisialService.desactivar(id);
      } else {
        await lumisialService.activar(id);
      }
      await cargarDatos();
      setToast({
        type: 'success',
        message: `Lumisial ${activo ? 'desactivado' : 'activado'} exitosamente`
      });
    } catch (error) {
      console.error('Error al cambiar estado del lumisial:', error);
      setToast({ type: 'error', message: 'Error al cambiar estado del lumisial' });
    }
  };

  const handleSaveProvinciaEclesiastica = async (provinciaData: Omit<ProvinciaEclesiastica, 'id' | 'created_at'>) => {
    try {
      if (selectedProvinciaEclesiastica) {
        await provinciaEclesiasticaService.actualizar(selectedProvinciaEclesiastica.id, provinciaData);
        setToast({ type: 'success', message: 'Provincia eclesiástica actualizada exitosamente' });
      } else {
        await provinciaEclesiasticaService.crear(provinciaData);
        setToast({ type: 'success', message: 'Provincia eclesiástica creada exitosamente' });
      }
      await cargarDatos();
      setIsProvinciaEclesiasticaModalOpen(false);
      setSelectedProvinciaEclesiastica(undefined);
    } catch (error) {
      console.error('Error al guardar provincia eclesiástica:', error);
      throw error;
    }
  };

  const handleSaveDiocesis = async (diocesisData: Omit<Diocesis, 'id' | 'created_at' | 'provincia_eclesiastica' | 'cantidad_lumisiales'>): Promise<string> => {
    try {
      let diocesisId: string;
      if (selectedDiocesis) {
        const updated = await diocesisService.actualizar(selectedDiocesis.id, diocesisData);
        diocesisId = updated.id;
        setToast({ type: 'success', message: 'Diócesis actualizada exitosamente' });
      } else {
        const created = await diocesisService.crear(diocesisData);
        diocesisId = created.id;
        setToast({ type: 'success', message: 'Diócesis creada exitosamente' });
      }
      await cargarDatos();
      setIsDiocesisModalOpen(false);
      setSelectedDiocesis(undefined);
      return diocesisId;
    } catch (error) {
      console.error('Error al guardar diócesis:', error);
      throw error;
    }
  };

  const handleSuccessLumisial = async () => {
    await cargarDatos();
    setToast({
      type: 'success',
      message: `Lumisial ${selectedLumisial ? 'actualizado' : 'creado'} exitosamente`
    });
  };

  const handleOpenProvinciaEclesiasticaModal = () => {
    setSelectedProvinciaEclesiastica(undefined);
    setIsProvinciaEclesiasticaModalOpen(true);
  };

  const handleEditProvinciaEclesiastica = (provincia: ProvinciaEclesiastica) => {
    setSelectedProvinciaEclesiastica(provincia);
    setIsProvinciaEclesiasticaModalOpen(true);
  };

  const handleOpenDiocesisModal = () => {
    setSelectedDiocesis(undefined);
    setIsDiocesisModalOpen(true);
  };

  const handleEditDiocesis = (diocesis: Diocesis) => {
    setSelectedDiocesis(diocesis);
    setIsDiocesisModalOpen(true);
  };

  const handleOpenLumisialModal = (lumisial?: Lumisial) => {
    setSelectedLumisial(lumisial);
    setIsLumisialModalOpen(true);
  };

  const handleCloseLumisialModal = () => {
    setIsLumisialModalOpen(false);
    setSelectedLumisial(undefined);
  };

  const limpiarFiltros = () => {
    setFiltros({
      provincia_eclesiastica_id: '',
      diocesis_id: '',
      region: '',
      estado: 'todos'
    });
  };

  const isUserAdminGlobal = usuario?.rol?.nombre ? isAdminGlobal(usuario.rol.nombre) : false;

  const handleOpenEliminacionModal = (
    tipo: 'provincia' | 'diocesis' | 'lumisial',
    id: string,
    nombre: string
  ) => {
    // Limpiar cualquier estado previo antes de abrir el modal
    setIsEliminacionModalOpen(false);
    setEntidadParaEliminar(null);
    
    // Pequeño retraso para asegurar que el estado se limpie
    setTimeout(() => {
      setEntidadParaEliminar({ tipo, id, nombre });
      setIsEliminacionModalOpen(true);
    }, 50);
  };

  const handleSuccessEliminacion = async () => {
    await cargarDatos();

    // Cerrar el modal y limpiar el estado después de recargar los datos
    setIsEliminacionModalOpen(false);
    setEntidadParaEliminar(null);
    
    setToast({
      type: 'success',
      message: 'Entidad eliminada definitivamente'
    });
  };

  const lumisialesActivos = lumisiales.filter(l => l.activo).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Geografía</h1>
          <p className="text-slate-600 mt-1">Administración de provincias eclesiásticas, diócesis, prefecturas y lumisiales</p>
        </div>
      </div>

      <Tabs
        tabs={[
          { id: 'provincias_eclesiasticas', label: 'Provincias Eclesiásticas', icon: <Globe className="w-5 h-5" /> },
          { id: 'diocesis', label: 'Diócesis y Prefecturas', icon: <MapPin className="w-5 h-5" /> },
          { id: 'lumisiales', label: 'Lumisiales', icon: <Building2 className="w-5 h-5" /> }
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === 'provincias_eclesiasticas' && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200">
          <div className="p-6 border-b border-slate-200 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Provincias Eclesiásticas</h2>
              <p className="text-sm text-slate-600 mt-1">
                {provinciasEclesiasticas.filter(p => p.activo).length} provincias eclesiásticas activas
              </p>
            </div>
            <Button variant="default" onClick={handleOpenProvinciaEclesiasticaModal}>
              <Plus className="w-4 h-4 mr-2" />
              Agregar Provincia Eclesiástica
            </Button>
          </div>

          <Table
            columns={[
              { 
                key: 'nombre', 
                label: 'Provincia Eclesiástica',
                render: (provincia) => (
                  <span className="text-slate-800">
                    {provincia.nombre || <span className="text-slate-400 italic">Sin nombre</span>}
                  </span>
                )
              },
              { key: 'codigo_iso', label: 'Código ISO' },
              {
                key: 'continente',
                label: 'Continente',
                render: (provincia) => (
                  <span className="text-slate-700">
                    {(provincia as ProvinciaEclesiastica & { continente?: string }).continente || <span className="text-slate-400 italic">No especificado</span>}
                  </span>
                )
              },
              {
                key: 'activo',
                label: 'Estado',
                render: (provincia) => (
                  <Badge variant={provincia.activo ? 'success' : 'danger'}>
                    {provincia.activo ? 'Activo' : 'Inactivo'}
                  </Badge>
                )
              },
              {
                key: 'acciones',
                label: 'Acciones',
                render: (provincia) => (
                  <div className="flex gap-2">
                    <Tooltip content="Editar provincia eclesiástica">
                      <button
                        onClick={() => handleEditProvinciaEclesiastica(provincia)}
                        className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    <Tooltip content={provincia.activo ? 'Desactivar provincia eclesiástica' : 'Activar provincia eclesiástica'}>
                      <button
                        onClick={() => handleToggleProvinciaEclesiasticaActivo(provincia.id, provincia.activo)}
                        className={`p-2 rounded-lg transition-colors ${
                          provincia.activo
                            ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                            : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                        }`}
                      >
                        {provincia.activo ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                      </button>
                    </Tooltip>
                    {isUserAdminGlobal && (
                      <Tooltip content="Eliminar definitivamente provincia eclesiástica">
                        <button
                          onClick={() => handleOpenEliminacionModal('provincia', provincia.id, provincia.nombre)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                )
              }
            ]}
            data={provinciasEclesiasticas}
            emptyMessage="No hay provincias eclesiásticas registradas"
          />
        </div>
      )}

      {activeTab === 'diocesis' && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200">
          <div className="p-6 border-b border-slate-200 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Diócesis y Prefecturas</h2>
              <p className="text-sm text-slate-600 mt-1">
                {diocesis.filter(d => d.activo).length} diócesis/prefecturas activas
              </p>
            </div>
            <Button variant="default" onClick={handleOpenDiocesisModal}>
              <Plus className="w-4 h-4 mr-2" />
              Agregar Diócesis/Prefectura
            </Button>
          </div>

          <Table
            columns={[
              { key: 'nombre', label: 'Nombre' },
              {
                key: 'tipo',
                label: 'Tipo',
                render: (diocesis) => (
                  <Badge variant={diocesis.tipo === 'diocesis' ? 'info' : 'warning'}>
                    {diocesis.tipo === 'diocesis' ? 'Diócesis' : 'Prefectura'}
                  </Badge>
                )
              },
              {
                key: 'provincia_eclesiastica',
                label: 'Provincia Eclesiástica',
                render: (diocesis) => diocesis.provincia_eclesiastica?.nombre || '-'
              },
              {
                key: 'lumisiales',
                label: 'Lumisiales',
                render: (diocesis) => (
                  <span className="text-sm font-medium text-slate-700">
                    {diocesis.cantidad_lumisiales || 0}
                  </span>
                )
              },
              {
                key: 'activo',
                label: 'Estado',
                render: (diocesis) => (
                  <Badge variant={diocesis.activo ? 'success' : 'danger'}>
                    {diocesis.activo ? 'Activa' : 'Inactiva'}
                  </Badge>
                )
              },
              {
                key: 'acciones',
                label: 'Acciones',
                render: (diocesis) => (
                  <div className="flex gap-2">
                    <Tooltip content="Editar diócesis/prefectura">
                      <button
                        onClick={() => handleEditDiocesis(diocesis)}
                        className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    <Tooltip content={diocesis.activo ? 'Desactivar diócesis/prefectura' : 'Activar diócesis/prefectura'}>
                      <button
                        onClick={() => handleToggleDiocesisActiva(diocesis.id, diocesis.activo)}
                        className={`p-2 rounded-lg transition-colors ${
                          diocesis.activo
                            ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                            : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                        }`}
                      >
                        {diocesis.activo ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                      </button>
                    </Tooltip>
                    {isUserAdminGlobal && (
                      <Tooltip content="Eliminar definitivamente diócesis/prefectura">
                        <button
                          onClick={() => handleOpenEliminacionModal('diocesis', diocesis.id, diocesis.nombre)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                )
              }
            ]}
            data={diocesis}
            emptyMessage="No hay diócesis o prefecturas registradas"
          />
        </div>
      )}

      {activeTab === 'lumisiales' && (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-slate-600" />
              <h2 className="text-lg font-semibold text-slate-900">Filtros</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Provincia Eclesiástica
                </label>
                <Combobox
                  placeholder="Seleccionar provincia eclesiástica..."
                  options={provinciasOptions}
                  value={filtros.provincia_eclesiastica_id}
                  onChange={(value) => setFiltros({ ...filtros, provincia_eclesiastica_id: value, diocesis_id: '' })}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Diócesis/Prefectura
                </label>
                <Combobox
                  placeholder={filtros.provincia_eclesiastica_id ? "Seleccionar diócesis..." : "Seleccione provincia"}
                  options={diocesisOptions}
                  value={filtros.provincia_eclesiastica_id ? filtros.diocesis_id : 'DISABLED'}
                  onChange={(value) => setFiltros({ ...filtros, diocesis_id: value, region: '' })}
                  className="w-full"
                  disabled={!filtros.provincia_eclesiastica_id}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Región
                </label>
                <Combobox
                  placeholder={filtros.diocesis_id ? "Seleccionar región..." : "Seleccione diócesis"}
                  options={regionesFiltradas}
                  value={filtros.diocesis_id ? filtros.region : 'DISABLED'}
                  onChange={(value) => setFiltros({ ...filtros, region: value })}
                  className="w-full"
                  disabled={!filtros.diocesis_id}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Estado
                </label>
                <Select
                  value={filtros.estado}
                  onChange={(value) => setFiltros({ ...filtros, estado: value })}
                  options={[
                    { value: 'todos', label: 'Todos' },
                    { value: 'activos', label: 'Solo activos' },
                    { value: 'inactivos', label: 'Solo inactivos' }
                  ]}
                />
              </div>

              <div className="flex items-end">
                <Button
                  variant="default"
                  onClick={limpiarFiltros}
                  className="w-full"
                >
                  Limpiar Filtros
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Lumisiales</h2>
                <p className="text-sm text-slate-600 mt-1">
                  {lumisialesActivos} lumisiales activos de {lumisiales.length} totales
                  {lumisialesFiltrados.length !== lumisiales.length && (
                    <span className="ml-2 text-blue-600 font-medium">
                      (mostrando {lumisialesFiltrados.length})
                    </span>
                  )}
                </p>
              </div>
              <Button variant="default" onClick={() => handleOpenLumisialModal()}>
                <Plus className="w-4 h-4 mr-2" />
                Agregar Lumisial
              </Button>
            </div>

            <Table
              columns={[
                { key: 'nombre', label: 'Nombre' },
                {
                  key: 'diocesis',
                  label: 'Diócesis/Prefectura',
                  render: (lumisial) => (
                    <div className="flex items-center gap-2">
                      <span>{lumisial.diocesis?.nombre || '-'}</span>
                      {lumisial.diocesis && (
                        <Badge variant={lumisial.diocesis.tipo === 'diocesis' ? 'info' : 'warning'}>
                          {lumisial.diocesis.tipo === 'diocesis' ? 'D' : 'P'}
                        </Badge>
                      )}
                    </div>
                  )
                },
                { key: 'ciudad', label: 'Ciudad' },
                {
                  key: 'region',
                  label: 'Región',
                  render: (lumisial) => lumisial.region || '-'
                },
                {
                  key: 'provincia_eclesiastica',
                  label: 'Provincia Eclesiástica',
                  render: (lumisial) => lumisial.provincia_eclesiastica?.nombre || '-'
                },
                {
                  key: 'miembros',
                  label: 'Miembros',
                  render: (lumisial) => (
                    <span className="text-sm font-medium text-slate-700">
                      {lumisial.cantidad_miembros || 0}
                    </span>
                  )
                },
                {
                  key: 'activo',
                  label: 'Estado',
                  render: (lumisial) => (
                    <Badge variant={lumisial.activo ? 'success' : 'danger'}>
                      {lumisial.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  )
                },
                {
                  key: 'acciones',
                  label: 'Acciones',
                  render: (lumisial) => (
                    <div className="flex gap-2">
                      <Tooltip content="Editar lumisial">
                        <button
                          onClick={() => handleOpenLumisialModal(lumisial)}
                          className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content={lumisial.activo ? 'Desactivar lumisial' : 'Activar lumisial'}>
                        <button
                          onClick={() => handleToggleLumisialActivo(lumisial.id, lumisial.activo)}
                          className={`p-2 rounded-lg transition-colors ${
                            lumisial.activo
                              ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                              : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                          }`}
                        >
                          {lumisial.activo ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                        </button>
                      </Tooltip>
                      {isUserAdminGlobal && (
                        <Tooltip content="Eliminar definitivamente lumisial">
                          <button
                            onClick={() => handleOpenEliminacionModal('lumisial', lumisial.id, lumisial.nombre)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  )
                }
              ]}
              data={lumisialesFiltrados}
              emptyMessage="No hay lumisiales registrados"
            />
          </div>
        </>
      )}

      {toast && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md animate-in slide-in-from-right duration-300">
          <div className={`flex items-start gap-3 p-4 rounded-lg border-2 shadow-lg ${
            toast.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : 'bg-red-50 text-red-800 border-red-200'
          }`}>
            {toast.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            )}
            <p className="flex-1 text-sm font-medium">{toast.message}</p>
            <button
              onClick={() => setToast(null)}
              className="flex-shrink-0 p-1 hover:bg-black/10 rounded transition-colors"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <ProvinciaEclesiasticaModal
        isOpen={isProvinciaEclesiasticaModalOpen}
        onClose={() => {
          setIsProvinciaEclesiasticaModalOpen(false);
          setSelectedProvinciaEclesiastica(undefined);
        }}
        onSave={handleSaveProvinciaEclesiastica}
        provinciaEclesiastica={selectedProvinciaEclesiastica}
      />

      <DiocesisModal
        isOpen={isDiocesisModalOpen}
        onClose={() => {
          setIsDiocesisModalOpen(false);
          setSelectedDiocesis(undefined);
        }}
        onSave={handleSaveDiocesis}
        diocesis={selectedDiocesis}
      />

      <LumisialModal
        isOpen={isLumisialModalOpen}
        onClose={handleCloseLumisialModal}
        onSuccess={handleSuccessLumisial}
        lumisial={selectedLumisial}
      />

      {entidadParaEliminar && (
        <EliminacionDefinitivaModal
          isOpen={isEliminacionModalOpen}
          onClose={() => {
            setIsEliminacionModalOpen(false);
            setEntidadParaEliminar(null);
          }}
          tipoEntidad={entidadParaEliminar.tipo}
          entidadId={entidadParaEliminar.id}
          entidadNombre={entidadParaEliminar.nombre}
          onSuccess={handleSuccessEliminacion}
        />
      )}
    </div>
  );
}
